<template>
  <div>
    <h2 class="text-lg font-semibold text-gray-800 mb-1">Cost &amp; Utilization</h2>
    <div class="flex items-center gap-3 mb-4 flex-wrap">
      <span class="filter-label">Care Setting</span>
      <select v-model="careSetting" class="ctrl" @change="fetchAll">
        <option value="">All</option>
        <option value="SNF">SNF</option>
        <option value="ALF">ALF</option>
      </select>
      <span class="filter-label">Group By</span>
      <select v-model="entityType" class="ctrl" @change="onEntityTypeChange">
        <option value="Facility">Facility</option>
        <option value="Staff">Provider</option>
      </select>
      <span class="filter-label">Time Window</span>
      <select v-model="timeWindow" class="ctrl">
        <option value="ytd">YTD</option>
        <option value="rolling12">Rolling 12 Mo</option>
        <option value="beur">BEUR Benchmark</option>
      </select>
      <template v-if="reportDates.length">
        <span class="filter-label">Attribution Panel</span>
        <select v-model="reportDt" class="ctrl" @change="fetchAll">
          <option v-for="d in reportDates" :key="d" :value="d">{{ d }}</option>
        </select>
      </template>
      <button class="ctrl hover:bg-gray-50" @click="saveView">{{ savedViewJustSaved ? 'Saved ✓' : 'Save View' }}</button>
      <button v-if="hasSavedView" class="ctrl hover:bg-gray-50" @click="resetView">Reset</button>
      <ExportMenu :disabled="!currentRows.length" :exports="[{ type: 'csv', action: exportCsv }]" />
    </div>

    <div v-if="loading" class="flex items-center justify-center py-20">
      <LoadingIndicator message="Loading cost data…" />
    </div>

    <!-- Drilldown: provider → facility -->
    <template v-else-if="selectedProvider">
      <div class="mb-3">
        <button class="text-xs text-gray-500 hover:text-gray-700" @click="clearDrilldown">← Back to all providers</button>
        <h3 class="text-sm font-semibold text-gray-800 mt-1"><span class="sensitive">{{ selectedProvider.name }}</span> — Facility Breakdown</h3>
      </div>
      <div v-if="drilldownRows.length" class="ext-table-wrap scroll-x scroll-y">
        <table class="ext-table">
          <thead><tr>
            <th style="padding-left:16px; max-width:160px;">Facility</th>
            <th v-for="col in numColumns" :key="col.key" class="num">{{ col.header }}</th>
          </tr></thead>
          <tbody>
            <tr v-for="row in drilldownRows" :key="row.id">
              <td class="metric-name">{{ row.name }}</td>
              <td v-for="col in numColumns" :key="col.key" class="num">{{ col.fmt(row[col.key]) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="py-10 text-center text-xs text-gray-400">No facility data for this provider.</div>
    </template>

    <!-- Main table -->
    <template v-else>
      <div v-if="currentTotals" class="benchmark-banner">
        <div class="benchmark-label">Practice Average</div>
        <div class="benchmark-stats">
          <div class="benchmark-stat"><span class="benchmark-value">{{ fmtCurrency(currentTotals.pbpm) }}</span><span class="benchmark-key">Total PBPM</span></div>
          <div class="benchmark-stat"><span class="benchmark-value">{{ fmtDec1(currentTotals.admits_per_k) }}</span><span class="benchmark-key">IP Admits / 1K</span></div>
          <div class="benchmark-stat"><span class="benchmark-value">{{ fmtDec1(currentTotals.snf_days_per_k) }}</span><span class="benchmark-key">SNF Days / 1K</span></div>
          <div class="benchmark-stat"><span class="benchmark-value sensitive">{{ fmtInt(currentTotals.patients) }}</span><span class="benchmark-key">Patients</span></div>
        </div>
      </div>

      <div v-if="currentRows.length" class="ext-table-wrap scroll-x scroll-y">
        <table class="ext-table">
          <thead>
            <tr>
              <th style="padding-left:16px; max-width:160px; cursor:pointer;" @click="toggleSort('name')">
                {{ entityType === 'Facility' ? 'Facility' : 'Provider' }}
                <span v-if="sortField === 'name'" style="margin-left:2px; font-size:8px;">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th v-for="col in numColumns" :key="col.key" class="num" style="cursor:pointer;" @click="toggleSort(col.key)">
                {{ col.header }}
                <span v-if="sortField === col.key" style="margin-left:2px; font-size:8px;">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
              </th>
            </tr>
            <tr v-if="currentTotals" class="totals-row">
              <td style="padding-left:16px; font-weight:600; color:#221F1C;">Total</td>
              <td v-for="col in numColumns" :key="'t-'+col.key" class="num">{{ col.fmt(currentTotals[col.key]) }}</td>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in sortedRows" :key="row.id"
              :style="entityType === 'Staff' ? 'cursor:pointer;' : ''"
              @click="entityType === 'Staff' ? drilldown(row) : null">
              <td class="metric-name">{{ row.name }}</td>
              <td v-for="col in numColumns" :key="col.key" class="num" :style="heatStyle(row[col.key], col.key)">
                {{ col.fmt(row[col.key]) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else-if="!loading" class="flex items-center justify-center py-20" style="color:#A5A5A6; font-size:13px;">No data available</div>
    </template>
  </div>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import savedView from '@/mixins/savedView'
import { blockSensitiveEgress } from '@/utils/demoEgress'

export default {
  name: 'CCLFReport_CostUtilization',
  components: { LoadingIndicator, ExportMenu },
  mixins: [savedView],
  savedView: {
    key: 'cclf-cost-utilization',
    fields: ['entityType', 'timeWindow', 'careSetting', 'sortField', 'sortDir'],
    scopeProp: 'entityId',
  },
  props: {
    contractId: { type: String, default: null },
    performanceYear: { type: Number, default: null },
    dosthru: { type: String, default: null },
    entityId: { type: Number, default: null },
    initialCareSetting: { type: String, default: '' },
  },

  data() {
    return {
      careSetting: this.initialCareSetting || '',
      entityType: 'Facility',
      timeWindow: 'ytd',
      reportDates: [],
      reportDt: null,
      pageData: null,  // {ytd, rolling12, beur, totals, max_efctv_dt}
      loading: false,
      sortField: 'patients',
      sortDir: 'desc',
      selectedProvider: null,
      drilldownRows: [],
      rtaByEntity: {},
    }
  },

  computed: {
    numColumns() {
      // Column order per Curana request (Jennifer, 2026-06-26): utilization
      // rates (Admits/1K, SNF Days/1K) sit immediately after Patients so the
      // eye lands on attribution → utilization before the per-category cost
      // breakdown; total cost/PBPM remain the two rightmost summary columns.
      return [
        { key: 'patients', header: 'Patients', fmt: this.fmtInt },
        { key: 'admits_per_k', header: 'Admits/1K', fmt: this.fmtDec1 },
        { key: 'rta_pct', header: 'Return to Acute %', fmt: v => (v == null ? '—' : `${(v * 100).toFixed(1)}%`) },
        { key: 'snf_days_per_k', header: 'SNF Days/1K', fmt: this.fmtDec1 },
        { key: 'ip_pbpm', header: 'IP', fmt: this.fmtCurrency },
        { key: 'snf_pbpm', header: 'SNF', fmt: this.fmtCurrency },
        { key: 'op_pbpm', header: 'OP', fmt: this.fmtCurrency },
        { key: 'part_b_pbpm', header: 'Part B', fmt: this.fmtCurrency },
        { key: 'hh_dme_pbpm', header: 'DME/HHA', fmt: this.fmtCurrency },
        { key: 'hospice_pbpm', header: 'Hospice', fmt: this.fmtCurrency },
        { key: 'pbpm', header: 'Total PBPM', fmt: this.fmtCurrency },
        { key: 'total_cost', header: 'Total Cost', fmt: this.fmtCurrency },
      ]
    },
    currentRows() {
      if (!this.pageData) return []
      return (this.pageData[this.timeWindow] || []).map(r => ({ ...r, rta_pct: this.rtaByEntity[r.id] ?? null }))
    },
    currentTotals() {
      if (!this.pageData?.totals) return null
      return this.pageData.totals[this.timeWindow]
    },
    sortedRows() {
      const rows = [...this.currentRows]
      const f = this.sortField, dir = this.sortDir === 'asc' ? 1 : -1
      if (f === 'name') rows.sort((a, b) => (a.name || '').localeCompare(b.name || '') * dir)
      else rows.sort((a, b) => ((a[f] || 0) - (b[f] || 0)) * dir)
      return rows
    },
    columnMaxes() {
      const maxes = {}
      for (const col of this.numColumns) maxes[col.key] = Math.max(0, ...this.currentRows.map(r => r[col.key] || 0))
      return maxes
    },
  },

  watch: {
    contractId() { this.fetchAll() },
    performanceYear() { this.fetchAll() },
    entityId() { this.fetchAll() },
  },

  async mounted() {
    await this.fetchReportDates()
    this.fetchAll()
  },

  methods: {
    // Called by the mixin's resetView() after it clears storage.
    onResetView() {
      this.entityType = 'Facility'
      this.timeWindow = 'ytd'
      this.careSetting = this.initialCareSetting || ''
      this.sortField = 'patients'
      this.sortDir = 'desc'
      this.selectedProvider = null
      this.drilldownRows = []
      this.fetchAll()
    },
    async fetchReportDates() {
      if (!this.contractId) return
      try {
        const resp = await this.$axios.post('/api/claim-explorer/report-dates/', { contract_id: this.contractId })
        this.reportDates = resp.data || []
        if (this.reportDates.length && !this.reportDt) this.reportDt = this.reportDates[0]
      } catch { this.reportDates = [] }
    },

    onEntityTypeChange() {
      this.selectedProvider = null
      this.drilldownRows = []
      this.fetchAll()
    },

    toggleSort(field) {
      if (this.sortField === field) this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      else { this.sortField = field; this.sortDir = field === 'name' ? 'asc' : 'desc' }
    },

    async fetchAll() {
      if (!this.contractId || !this.performanceYear) return
      this.loading = true
      this.selectedProvider = null
      this.drilldownRows = []
      try {
        const resp = await this.$axios.post('/api/claim-explorer/entity-cost-util/', {
          entity_id: this.entityId,
          contract_id: this.contractId,
          report_dt: this.reportDt || `${this.performanceYear - 1}-12-01`,
          entity_type: this.entityType,
          care_setting: this.careSetting || undefined,
        })
        this.pageData = resp.data
        this.fetchReturnToAcute()
      } catch (e) {
        console.error('Failed to load cost/utilization:', e)
        this.pageData = null
      } finally {
        this.loading = false
      }
    },

    async fetchReturnToAcute() {
      this.rtaByEntity = {}
      if (this.entityType !== 'Facility') return
      try {
        const { data } = await this.$axios.post('/api/impact_analytics/composable-metrics/run_multi/', {
          metric_ids: [707],
          entity_type: 'Facility',
          entity_id: this.entityId,
          observation_year: this.performanceYear,
          time_series: false,
          group_by_contract: false,
        })
        const map = {}
        for (const r of data?.results || []) {
          if (r.rate != null && map[r.entity_id] === undefined) map[r.entity_id] = r.rate
        }
        this.rtaByEntity = map
      } catch {
        this.rtaByEntity = {}
      }
    },

    async drilldown(row) {
      this.selectedProvider = row
      this.loading = true
      try {
        const resp = await this.$axios.post('/api/claim-explorer/entity-cost-util/', {
          entity_id: row.id,
          contract_id: this.contractId,
          report_dt: this.reportDt || `${this.performanceYear - 1}-12-01`,
          entity_type: 'Facility',
          care_setting: this.careSetting || undefined,
        })
        this.drilldownRows = (resp.data?.[this.timeWindow]) || []
      } catch (e) {
        console.error('Failed to load facility drilldown:', e)
        this.drilldownRows = []
      } finally {
        this.loading = false
      }
    },

    clearDrilldown() { this.selectedProvider = null; this.drilldownRows = [] },

    heatStyle(val, field) {
      if (val == null || val <= 0) return null
      const max = this.columnMaxes[field]
      if (!max) return null
      const ratio = Math.min(val / max, 1)
      // Color by whether "high" is good or bad.
      //  - High cost / utilization = bad → red.
      //  - High patient count = good (bigger panel) → blue.
      // Everything in this report (cost PBPMs, total cost, admits/1K,
      // SNF days/1K) is "high = bad" except the patient count.
      const goodWhenHigh = new Set(['patients'])
      return goodWhenHigh.has(field)
        ? { backgroundColor: `rgba(37, 99, 235, ${ratio * 0.45})` }   // blue
        : { backgroundColor: `rgba(220, 38, 38, ${ratio * 0.50})` }   // red
    },

    fmtCurrency(v) { return v == null ? '—' : '$' + Math.round(v).toLocaleString('en-US') },
    fmtInt(v) { return v == null ? '—' : Math.round(v).toLocaleString('en-US') },
    fmtDec1(v) { return v == null ? '—' : Number(v).toFixed(1) },

    exportCsv() {
      const rows = this.sortedRows
      if (!rows.length) return
      if (blockSensitiveEgress('export')) return
      const label = this.entityType === 'Facility' ? 'Facility' : 'Provider'
      const cols = [{ key: 'name', header: label }, ...this.numColumns]
      const header = cols.map(c => c.header).join(',')
      const lines = rows.map(r => cols.map(c => {
        const v = r[c.key]
        if (v == null) return ''
        return c.key === 'name' ? `"${String(v).replace(/"/g, '""')}"` : Math.round(v * 100) / 100
      }).join(','))
      const csv = [header, ...lines].join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = `cost_utilization_${this.entityType.toLowerCase()}_${this.timeWindow}.csv`
      a.click(); URL.revokeObjectURL(url)
    },
  },
}
</script>

<style scoped>
td.metric-name { max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.filter-label { font-size: 11px; font-weight: 600; color: #7D7770; }
.ctrl { padding: 4px 10px; font-size: 12px; border: 1px solid #D1D5DB; border-radius: 5px; background: #fff; color: #374151; font-family: inherit; }
.ext-table thead { position: sticky; top: 0; z-index: 1; }
.ext-table thead th { background: #fff; }
.totals-row td { font-family: 'JetBrains Mono', 'SF Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; font-size: 11.5px; font-weight: 600; color: #221F1C; padding: 8px 10px; border-bottom: 2px solid #E5E0D9; background: #FAF9F6; text-align: right; }
.benchmark-banner { background: #fff; border: 1px solid #F2EEE8; border-radius: 8px; padding: 14px 20px; margin-bottom: 12px; display: flex; align-items: center; gap: 24px; }
.benchmark-label { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; color: #7D7770; white-space: nowrap; }
.benchmark-stats { display: flex; gap: 20px; flex-wrap: wrap; }
.benchmark-stat { display: flex; flex-direction: column; align-items: center; gap: 2px; }
.benchmark-value { font-family: 'JetBrains Mono', 'SF Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; font-size: 15px; font-weight: 600; color: #0F0D0B; }
.benchmark-key { font-size: 9px; color: #A9A29A; text-transform: uppercase; letter-spacing: 0.06em; }
.scroll-x { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.scroll-y { max-height: 70vh; overflow-y: auto; }
</style>
